import { ThemeCacheEntry, ThemeTokens } from '@/types/theme';

export const THEME_CACHE_PREFIX = 'bk:theme:v2:';

const ASSET_FILE_PATTERN = /^.+\.[a-zA-Z0-9]+$/;

function canUseWindow() {
  return typeof window !== 'undefined';
}

export function getThemeCacheKey(slug: string) {
  return `${THEME_CACHE_PREFIX}${slug}`;
}

export function getAlbumSlugFromPathname(pathname: string): string | null {
  const path = pathname.trim();

  if (!path || path === '/' || path === '/index.html') {
    return null;
  }

  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  const firstSegment = segments[0];
  if (
    firstSegment === 'panel' ||
    firstSegment === 's' ||
    firstSegment.startsWith('.') ||
    ASSET_FILE_PATTERN.test(firstSegment)
  ) {
    return null;
  }

  return firstSegment;
}

function isThemeCacheEntry(value: unknown): value is ThemeCacheEntry {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const entry = value as Partial<ThemeCacheEntry>;
  return (
    typeof entry.slug === 'string' &&
    typeof entry.selectionKey === 'string' &&
    typeof entry.cachedAt === 'number' &&
    !!entry.tokens &&
    typeof entry.tokens === 'object' &&
    !!entry.tokens.colors &&
    typeof entry.tokens.colors === 'object'
  );
}

export function readThemeCache(slug?: string | null): ThemeCacheEntry | null {
  if (!canUseWindow() || !slug) {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(getThemeCacheKey(slug));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!isThemeCacheEntry(parsed) || parsed.slug !== slug) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function writeThemeCache(entry: Omit<ThemeCacheEntry, 'cachedAt'> & { cachedAt?: number }) {
  if (!canUseWindow()) {
    return;
  }

  const nextEntry: ThemeCacheEntry = {
    ...entry,
    cachedAt: entry.cachedAt ?? Date.now(),
  };

  window.localStorage.setItem(getThemeCacheKey(entry.slug), JSON.stringify(nextEntry));
  window.localStorage.removeItem('cached_theme_tokens');
  window.localStorage.removeItem('theme_id');
}

export function writeThemeCacheForSlug(params: {
  slug?: string | null;
  selectionKey: string;
  themeId?: string;
  tokens: ThemeTokens;
}) {
  if (!params.slug) {
    return;
  }

  writeThemeCache({
    slug: params.slug,
    selectionKey: params.selectionKey || 'default',
    themeId: params.themeId,
    tokens: params.tokens,
  });
}

export function getBootstrappedTheme(): ThemeCacheEntry | null {
  if (!canUseWindow()) {
    return null;
  }

  const entry = window.__BK_THEME_BOOTSTRAP__;
  return isThemeCacheEntry(entry) ? entry : null;
}
